/**
 * Resolves backend API endpoints at runtime.
 *
 * All server-backed actions now go through Vercel API routes under `/api/*`.
 * Older Supabase Edge Function deployments may remain live during verification,
 * but client code should not route new traffic to `/functions/v1/*`.
 */
import { supabase } from "@/integrations/supabase/client";

const SUPABASE_ANON_KEY =
  (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined) ?? "";

/**
 * Resolve a backend endpoint URL. Accepts either a bare name ("send-push"),
 * a path-prefixed form ("/send-push") or the legacy "/api/send-push".
 * Query strings are preserved.
 */
export function apiUrl(path: string): string {
  const cleaned = path.replace(/^\/+/, "").replace(/^api\//, "");
  return `/api/${cleaned}`;
}

/**
 * Drop-in fetch wrapper. Resolves the URL via apiUrl() and injects the current
 * Supabase access token when available. Caller-supplied headers always win.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = apiUrl(path);
  const callerHeaders = new Headers(init.headers ?? {});
  if (!callerHeaders.has("authorization")) {
    let token: string | undefined;
    try {
      const { data } = await supabase.auth.getSession();
      token = data.session?.access_token ?? SUPABASE_ANON_KEY;
    } catch {
      token = SUPABASE_ANON_KEY || undefined;
    }
    if (token) callerHeaders.set("Authorization", `Bearer ${token}`);
  }
  return fetch(url, { ...init, headers: callerHeaders });
}
